import Markdown from "react-markdown";
import {
  FragmentType,
  getFragmentData,
  graphql,
} from "../../__generated__/gql";
import { ArticleStatus } from "../../__generated__/gql/graphql";
import StatusBadge, {
  StatusBadgeFragment,
} from "../../components/StatusBadge/StatusBadge";
import EmptyState from "../../ui/EmptyState/EmptyState";
import { PlayCircle, Zap } from "../../ui/Icon/Icon";
import Badge from "../../ui/Badge/Badge";
import styles from "./AdminArticleViewer.module.scss";

function ArticleEmptyState({
  title,
  status,
  data,
}: {
  title?: string | null;
  status: ArticleStatus;
  processingMessage?: string;
  data: FragmentType<typeof StatusBadgeFragment>;
}) {
  if (status === ArticleStatus.Processing) {
    return (
      <EmptyState
        title={title}
        icon={<Zap size="large" />}
        buttonText="Generate Article"
      >
        <StatusBadge data={data} />
      </EmptyState>
    );
  }

  return (
    <EmptyState
      title={title}
      icon={<Zap size="large" />}
      buttonText="Generate article"
      buttonIcon={<PlayCircle size="medium" />}
    />
  );
}

const AdminArticleViewerFragment = graphql(/* GraphQL */ `
  fragment AdminArticleViewer on AdminArticle {
    id
    title
    excerpt
    metaDescription
    processingMessage
    status
    body
    heroImage {
      id
      url
      alt
    }
    tag {
      id
      name
    }
    ...StatusBadge
  }
`);

function AdminArticleViewer({
  data,
}: {
  data: FragmentType<typeof AdminArticleViewerFragment>;
}) {
  const article = getFragmentData(AdminArticleViewerFragment, data);

  if (
    article.body &&
    article.title &&
    article.excerpt &&
    article.metaDescription &&
    article.status !== ArticleStatus.Error &&
    (article.status !== ArticleStatus.Processing ||
      !!article.processingMessage?.startsWith("Regenerating section"))
  ) {
    return (
      <div className={styles.pageLeftInner} id="article">
        <div>
          {article.heroImage && (
            <img
              className={styles.heroImage}
              src={article.heroImage.url}
              alt={article.heroImage.alt ?? ""}
            />
          )}
          <div className={styles.textContent}>
            {article.tag && (
              <Badge
                showDot={false}
                variant="badge"
                size="medium"
                color="default"
                className={styles.tag}
              >
                {article.tag.name}
              </Badge>
            )}
          </div>
        </div>
        <div className={styles.articleContent}>
          <h1>{article.title}</h1>
          <p>{article.excerpt}</p>
          <Markdown>{article.body}</Markdown>
        </div>
      </div>
    );
  }

  return (
    <ArticleEmptyState
      title={article.title}
      status={article.status}
      data={article}
    />
  );
}

export default AdminArticleViewer;
